import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { InvestigationHistoryItemData } from '@/redux/models';
import { AmlStatusEnum } from '@/api';
import { ChainsDictionary } from '@/config';
import {
  getFormattedDateWithTime,
  getIconByNetwork,
  getShortAddress,
} from '@/helpers';
import { RiskBadge, Button, TableFigure, CopyText } from '@/components';
import useDevice from '@/hooks/useDevice';
import styles from './InvestigationHistoryTableItem.module.sass';

interface InvestigationHistoryItemProps {
  item: InvestigationHistoryItemData;
  onDetails: (item: InvestigationHistoryItemData) => void;
  loading: boolean;
}

const InvestigationHistoryTableItem: FC<InvestigationHistoryItemProps> = (
  props,
) => {
  const { item, loading, onDetails } = props;
  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const networkIcon = useMemo(() => getIconByNetwork(item.network), []);
  const shortAddress = useMemo(() => getShortAddress(item.address), []);
  const fromNow = useMemo(() => getFormattedDateWithTime(item.timestamp), []);
  const networkName = useMemo(
    () => ChainsDictionary[item.network]?.name || item.network,
    [],
  );
  const hasRiskScore = item.riskscore !== null;
  const riskValue = item?.riskscore || 0;
  const isPending = item.status === AmlStatusEnum.PENDING;
  const incomeTxsCount =
    (item.connections?.directIncomes.length || 0) +
    (item.connections?.indirectIncomes.length || 0);
  const outcomeTxsCount =
    (item.connections?.directOutcomes.length || 0) +
    (item.connections?.indirectOutcomes.length || 0);
  const rowClasses = cx(styles.entry, {
    [styles.row]: true,
    [`${styles['is-pending']}`]: isPending,
  });

  const detailsHandler = useCallback(() => {
    onDetails(item);
  }, []);

  return (
    <div className={rowClasses}>
      <div className={`${styles.col} ${styles['col--icon']}`}>
        <img src={networkIcon} alt={item.network} />
      </div>
      <div className={`${styles.col} ${styles['col--name']}`}>
        <div className={styles.crypto}>
          <div className={styles.crypto__address}>
            <CopyText text={shortAddress} copyText={item.address} />
          </div>
          <div className={styles.crypto__info}>
            <span className={styles.crypto__network}>{networkName}</span>
            <span className={styles.crypto__asset}>{item?.displayAsset}</span>
          </div>
        </div>
      </div>

      {(!item?.indirects || item?.indirects?.connections.length === 0) && (
        <div className={`${styles.col} ${styles['col--nodata']}`}>
          {t('translation.investigation_block_nodata_subtitle')}
        </div>
      )}

      {item?.indirects?.connections.length > 0 && (
        <>
          <div className={`${styles.col} ${styles['col--status']}`}>
            {isPending && <span>Pending...</span>}
            {!isPending && (
              <>
                {hasRiskScore && (
                  <RiskBadge
                    value={riskValue}
                    note={
                      !isMobile && (
                        <span>
                          <b>{fromNow}</b>
                        </span>
                      )
                    }
                  />
                )}

                {!hasRiskScore && t('translation.tpl_aml_no_data_text')}

                {isMobile && (
                  <div className={styles.connections}>
                    <span className={styles.connections__income}>
                      ↑{incomeTxsCount}
                    </span>
                    <span className={styles.connections__outcome}>
                      ↓{outcomeTxsCount}
                    </span>
                  </div>
                )}

                {isMobile && hasRiskScore && (
                  <span className={styles.dateMobile}>{fromNow}</span>
                )}
              </>
            )}
          </div>

          <div className={`${styles.col} ${styles['col--figures']}`}>
            <span>
              <TableFigure
                figure={
                  <>
                    <b>{incomeTxsCount}</b>{' '}
                    <span>({t('translation.income_txs_count')})</span>
                    <br />
                    <b>{outcomeTxsCount}</b>{' '}
                    <span>({t('translation.outcome_txs_count')})</span>
                  </>
                }
                note={t('translation.connections')}
                className="complex"
              />
            </span>
          </div>
          <div className={`${styles.col} ${styles['col--actions']}`}>
            <Button
              size="m"
              type="secondary"
              onClick={detailsHandler}
              disabled={loading || isPending}
              nowrap
            >
              Details
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default InvestigationHistoryTableItem;
